/**
 * purchase-rules.js
 * Storefront cart guard for Horizon theme (drawer cart).
 *
 * Strategy:
 *  1. Patch window.fetch to intercept /cart/add calls before they hit Shopify
 *  2. Observe DOM for Horizon's checkout button (drawer renders it dynamically)
 *  3. Re-check on every cart update event Horizon dispatches
 */

(() => {
  "use strict";

  const TIER1 = "Tier1";
  const TIER2 = "Tier2";

  const MESSAGES = {
    QUOTA_EXHAUSTED:   "You have used all of your purchase quota. No further products can be added.",
    TIER1_EXHAUSTED:   "You have no remaining Tier 1 quota.",
    TIER2_EXHAUSTED:   "You have no remaining Tier 2 quota.",
    TIER_MIXING:       "You can\u2019t mix Tier 1 and Tier 2 products in the same order. Please clear your cart first.",
  };

  // ── Read meta tags injected by purchase-rules-init.liquid ──────────────────
  function metaInt(name, fallback) {
    const el = document.querySelector('meta[name="' + name + '"]');
    const v = parseInt(el ? el.getAttribute("content") : "", 10);
    return isNaN(v) ? fallback : v;
  }

  function getQuotas() {
    return {
      maxQuota:    metaInt("pr-max-quota",    5),
      remainTier1: metaInt("pr-remain-tier1", 0),
      remainTier2: metaInt("pr-remain-tier2", 0),
    };
  }

  // ── Current cart tiers via AJAX ────────────────────────────────────────────
  async function getCartTiers() {
    try {
      const res = await window._originalFetch("/cart.js");
      const cart = await res.json();
      const tiers = new Set();
      for (const item of (cart.items || [])) {
        const props = item.properties || {};
        const t = (props.tier || "").trim();
        if (t === TIER1 || t === TIER2) tiers.add(t);
      }
      return tiers;
    } catch (e) {
      return new Set();
    }
  }

  // ── Parse tier out of a /cart/add request body ─────────────────────────────
  function extractTierFromBody(body) {
    try {
      // Body can be JSON, FormData, or URLSearchParams
      if (typeof body === "string") {
        // JSON payload
        if (body.trim().startsWith("{")) {
          const parsed = JSON.parse(body);
          // Single item: { properties: { tier: "Tier1" } }
          if (parsed.properties && parsed.properties.tier) {
            return parsed.properties.tier.trim();
          }
          // Items array: { items: [{ properties: { tier: "Tier1" } }] }
          if (Array.isArray(parsed.items)) {
            for (const item of parsed.items) {
              const t = (item.properties && item.properties.tier) ? item.properties.tier.trim() : null;
              if (t) return t;
            }
          }
          return null;
        }
        // URLEncoded: properties[tier]=Tier1
        const params = new URLSearchParams(body);
        const t = params.get("properties[tier]");
        return t ? t.trim() : null;
      }
      if (body instanceof URLSearchParams) {
        const t = body.get("properties[tier]");
        return t ? t.trim() : null;
      }
      if (body instanceof FormData) {
        const t = body.get("properties[tier]");
        return t ? String(t).trim() : null;
      }
    } catch (e) {}
    return null;
  }

  // ── Banner ─────────────────────────────────────────────────────────────────
  const BANNER_ID = "pr-banner";

  function showBanner(msg) {
    removeBanner();
    const el = document.createElement("div");
    el.id = BANNER_ID;
    el.setAttribute("role", "alert");
    Object.assign(el.style, {
      position:        "fixed",
      top:             "0",
      left:            "0",
      width:           "100%",
      zIndex:          "99999",
      background:      "#b91c1c",
      color:           "#fff",
      padding:         "14px 20px",
      fontSize:        "15px",
      fontWeight:      "500",
      display:         "flex",
      justifyContent:  "space-between",
      alignItems:      "center",
      boxSizing:       "border-box",
      boxShadow:       "0 2px 8px rgba(0,0,0,0.3)",
    });
    const txt = document.createElement("span");
    txt.textContent = msg;
    const btn = document.createElement("button");
    btn.textContent = "\u00d7";
    btn.setAttribute("aria-label", "Dismiss");
    Object.assign(btn.style, {
      background: "none", border: "none", color: "#fff",
      fontSize: "22px", cursor: "pointer", marginLeft: "16px", lineHeight: "1",
    });
    btn.addEventListener("click", removeBanner);
    el.appendChild(txt);
    el.appendChild(btn);
    document.body.prepend(el);
    setTimeout(removeBanner, 8000);
  }

  function removeBanner() {
    const el = document.getElementById(BANNER_ID);
    if (el) el.remove();
  }

  // ── Core guard logic ───────────────────────────────────────────────────────
  async function getBlockReason(incomingTier) {
    const { remainTier1, remainTier2 } = getQuotas();
    const total = remainTier1 + remainTier2;

    if (total <= 0)                                    return MESSAGES.QUOTA_EXHAUSTED;
    if (incomingTier === TIER1 && remainTier1 <= 0)   return MESSAGES.TIER1_EXHAUSTED;
    if (incomingTier === TIER2 && remainTier2 <= 0)   return MESSAGES.TIER2_EXHAUSTED;

    const cartTiers = await getCartTiers();
    if (cartTiers.size > 0 && !cartTiers.has(incomingTier)) return MESSAGES.TIER_MIXING;

    return null;
  }

  // ── 1. Patch window.fetch to intercept /cart/add ───────────────────────────
  window._originalFetch = window.fetch.bind(window);

  window.fetch = async function (input, init) {
    const url = typeof input === "string" ? input : (input instanceof Request ? input.url : String(input));
    const isCartAdd = url.includes("/cart/add");

    if (isCartAdd) {
      const body = init && init.body ? init.body : null;
      const tier = body ? extractTierFromBody(
        // If body is a ReadableStream we can't parse it — fall through
        (body instanceof ReadableStream) ? null : body
      ) : null;

      if (tier) {
        const reason = await getBlockReason(tier);
        if (reason) {
          showBanner(reason);
          // Return a fake failed response so the drawer handles it gracefully
          return new Response(
            JSON.stringify({ status: 422, message: reason }),
            { status: 422, headers: { "Content-Type": "application/json" } }
          );
        }
      }
    }

    return window._originalFetch(input, init);
  };

  // ── 2. Also patch XMLHttpRequest for themes that use it ───────────────────
  const _XHROpen  = XMLHttpRequest.prototype.open;
  const _XHRSend  = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this._prUrl = url;
    return _XHROpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function (body) {
    if (this._prUrl && this._prUrl.includes("/cart/add") && body) {
      const tier = extractTierFromBody(body);
      if (tier) {
        getBlockReason(tier).then((reason) => {
          if (reason) showBanner(reason);
        });
        // Note: XHR can't be cancelled mid-send synchronously.
        // The server-side Cart Validation Function is the hard block here.
      }
    }
    return _XHRSend.apply(this, arguments);
  };

  // ── 3. Guard the checkout button (Horizon renders it inside the drawer) ────
  function guardCheckoutButton(btn) {
    if (btn._prGuarded) return;
    btn._prGuarded = true;
    btn.addEventListener("click", function (e) {
      const { remainTier1, remainTier2 } = getQuotas();
      if (remainTier1 + remainTier2 <= 0) {
        e.preventDefault();
        e.stopImmediatePropagation();
        showBanner(MESSAGES.QUOTA_EXHAUSTED);
      }
    }, true);
  }

  function attachCheckoutGuards() {
    // Horizon's drawer checkout button selectors
    const selectors = [
      'a[href="/checkout"]',
      'button[name="checkout"]',
      '[data-checkout-button]',
      'cart-drawer a[href*="checkout"]',
      '.cart__checkout-button',
      '.drawer__checkout',
    ];
    selectors.forEach((sel) => {
      document.querySelectorAll(sel).forEach(guardCheckoutButton);
    });
  }

  // ── 4. MutationObserver — Horizon injects drawer HTML dynamically ──────────
  const observer = new MutationObserver(() => {
    attachCheckoutGuards();
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // ── 5. Listen for Horizon's custom cart events ─────────────────────────────
  // Horizon dispatches these after drawer updates
  [
    "cart:updated",
    "cart:refresh",
    "drawer:open",
    "on:cart:updated",         // some Horizon versions
    "theme:cart:updated",
  ].forEach((evt) => {
    document.addEventListener(evt, attachCheckoutGuards);
    window.addEventListener(evt, attachCheckoutGuards);
  });

  // ── 6. Initial attach on DOM ready ────────────────────────────────────────
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", attachCheckoutGuards);
  } else {
    attachCheckoutGuards();
  }

})();